import React, { useState, useMemo, useEffect } from "react";
import {
  Chart,
  CommonSeriesSettings,
  Series,
  ValueAxis,
  ArgumentAxis,
  Label,
  Export,
  Format,
  Grid,
  Tooltip,
  Legend,
  Size,
  Title,
} from "devextreme-react/chart";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  Export as GridExport,
} from "devextreme-react/data-grid";
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useFetch, fetchWithToken } from "src/hooks/useFetch";
import { useAuth } from "src/contexts/auth";
import {
  telemetryInstrumentFolder,
  telemetryLogsRootFrom,
} from "src/utils/telemetry-logs-path";
import { SpatialRun } from "../types";

// ============ TypeScript Interfaces ============

interface UnassignedSequence {
  OccurrenceRate: number;
  Sequence: string;
}

interface TargetSiteAssignmentStat {
  TargetSiteName: string;
  RateAssigned?: number;
  RateMismatch?: number;
  UnassignedSequences?: UnassignedSequence[];
}

interface WellTargetAssignmentStat {
  WellLocation: string;
  TargetSiteAssignmentStats: TargetSiteAssignmentStat[];
}

interface TargetSiteCellAssignmentStat {
  TargetSiteName: string;
  RateAssignedPureCells?: number;
  RateAssignedMixedCells?: number;
  RateUnassignedMixedCells?: number;
  RateUnassignedLowCountCells?: number;
  AssignedCountsPerMM2?: number;
  MeanAssignedCountPerCell?: number;
  MedianAbundantTargetCount?: number;
  MeanUniqueTargetsPerCell?: number;
  ExtraCellularRatio?: number;
  RateTargetDropout?: number;
}

interface WellCellTargetAssignmentStat {
  WellLocation: string;
  TargetSiteCellAssignmentStats?: TargetSiteCellAssignmentStat[];
}

interface BatchTargetAssignmentData {
  BatchName: string;
  TargetSiteAssignmentStats?: TargetSiteAssignmentStat[];
  WellTargetAssignmentStats?: WellTargetAssignmentStat[];
}

// API returns an object with batch names as keys
type TargetAssignmentResponse = Record<string, BatchTargetAssignmentData>;

// Cell Assignment response structure
interface BatchCellAssignmentData {
  BatchName: string;
  WellCellTargetAssignmentStats?: WellCellTargetAssignmentStat[];
}

type CellAssignmentResponse = Record<string, BatchCellAssignmentData>;

// ============ Color Palette for Stacked Bars ============

const RATE_FIELD_COLORS = {
  RateAssignedPureCells: "#4CAF50",      // Green
  RateAssignedMixedCells: "#2196F3",     // Blue
  RateUnassignedMixedCells: "#FF9800",   // Orange
  RateUnassignedLowCountCells: "#F44336", // Red
};

const RATE_FIELD_LABELS = {
  RateAssignedPureCells: "Assigned Pure Cells",
  RateAssignedMixedCells: "Assigned Mixed Cells",
  RateUnassignedMixedCells: "Unassigned Mixed Cells",
  RateUnassignedLowCountCells: "Unassigned Low Count Cells",
};

/** Missing stats arrive as -999 or -9.99. Null leaves a chart gap; the table renders it as NaN. */
export const presentStat = (value: number | undefined | null): number | null => {
  if (value == null || Number.isNaN(value) || value < 0) return null;
  return value;
};

export const formatMissingStat = (
  value: number | null | undefined,
  options: { precision: number; style?: "number" | "percent" }
): string => {
  if (value == null || Number.isNaN(value)) return "NaN";
  if (options.style === "percent") {
    return `${(value * 100).toFixed(options.precision)}%`;
  }
  return value.toFixed(options.precision);
};

interface PanelDissTarget {
  TargetSiteName?: string;
  IsControl?: boolean;
  isControl?: boolean;
  iscontrol?: boolean;
}

interface PanelDissTube {
  BatchName?: string;
  DISSTargets?: PanelDissTarget[];
  Targets?: PanelDissTarget[];
}

export interface PanelDefinition {
  DISSPrimerTubes?: PanelDissTube[];
}

const TELEMETRY_LOGS_BUCKET =
  "elembio-prod-usw2-s3-aviti-telemetry-logs-usw2";

type PanelControlState =
  | { status: "loading" }
  | { status: "ready"; byBatch: Map<string, Set<string>> }
  | { status: "unavailable" };

function targetIsControl(target: PanelDissTarget): boolean {
  return (
    target.IsControl === true ||
    target.isControl === true ||
    target.iscontrol === true
  );
}

/** Control target sites keyed by specialized-batch name. */
export function controlTargetSitesByBatch(
  panel: PanelDefinition | null | undefined
): Map<string, Set<string>> {
  const byBatch = new Map<string, Set<string>>();
  for (const tube of panel?.DISSPrimerTubes ?? []) {
    const batchName = tube.BatchName?.trim();
    if (!batchName) continue;
    let sites = byBatch.get(batchName);
    if (!sites) {
      sites = new Set();
      byBatch.set(batchName, sites);
    }
    const targets = tube.DISSTargets ?? tube.Targets ?? [];
    for (const target of targets) {
      const name = target.TargetSiteName?.trim();
      if (name && targetIsControl(target)) sites.add(name);
    }
  }
  return byBatch;
}

export function isControlTargetSite(
  name: string | undefined | null,
  controlSites: ReadonlySet<string> | undefined
): boolean {
  if (!name || !controlSites || controlSites.size === 0) return false;
  return controlSites.has(name);
}

/** The rate bar chart is hidden for isControl sites. The statistics table still shows. */
export function showsCellAssignmentRateChart(
  targetSiteName: string | undefined | null,
  controlSites: ReadonlySet<string> | undefined
): boolean {
  return !isControlTargetSite(targetSiteName, controlSites);
}

export function panelJsonPath(run: SpatialRun): string {
  const tenantID = run.tenantID ? run.tenantID : "ten_traefikee";
  const root = telemetryLogsRootFrom(run);
  return `${TELEMETRY_LOGS_BUCKET}/${root}/${tenantID}/${telemetryInstrumentFolder(run.instrumentID, run.instrumentSerialNumber, run.instrumentName, run.model || run.instrumentModel)}/runs/${run.runID}/Panel.json`;
}

async function parsePanelPayload(data: unknown): Promise<PanelDefinition> {
  if (data instanceof Blob) {
    return JSON.parse(await data.text()) as PanelDefinition;
  }
  if (typeof data === "string") {
    return JSON.parse(data) as PanelDefinition;
  }
  return (data ?? {}) as PanelDefinition;
}

// ============ Main Component ============

interface CytoTargetAssignmentStatsProps {
  run: SpatialRun;
}

const CytoTargetAssignmentStats = ({ run }: CytoTargetAssignmentStatsProps) => {
  // Fetch Target Assignment data (for RateAssigned, RateMismatch, UnassignedSequences)
  const {
    data: rawData,
    isLoading,
    isError,
    error,
  } = useFetch(`/spatialRun/${run?.runID}/targetAssignment`, !!run?.runID);

  // Fetch Target Cell Assignment data (for Cell Assignment Stats)
  const {
    data: rawCellData,
    isLoading: isLoadingCell,
    isError: isErrorCell,
    error: errorCell,
  } = useFetch(`/spatialRun/${run?.runID}/targetCellAssignment`, !!run?.runID);

  const { getAccessTokenSilently } = useAuth();
  const [panelControls, setPanelControls] = useState<PanelControlState>({
    status: "loading",
  });

  const panelPath = run?.runID ? panelJsonPath(run) : "";

  useEffect(() => {
    if (!panelPath) return;
    let cancelled = false;
    setPanelControls({ status: "loading" });

    fetchWithToken(getAccessTokenSilently, "/thumbnailBytes", {
      method: "POST",
      body: JSON.stringify({ path: panelPath }),
    })
      .then(async (data) => {
        if (cancelled) return;
        const panel = await parsePanelPayload(data);
        setPanelControls({
          status: "ready",
          byBatch: controlTargetSitesByBatch(panel),
        });
      })
      .catch(() => {
        if (!cancelled) setPanelControls({ status: "unavailable" });
      });

    return () => {
      cancelled = true;
    };
  }, [panelPath, getAccessTokenSilently]);

  // Parse the response - it's an object with batch names as keys
  const responseData = rawData as TargetAssignmentResponse | undefined;
  const responseCellData = rawCellData as CellAssignmentResponse | undefined;

  // Get batch names from the response keys
  const batchNames = useMemo(() => {
    if (!responseData) return [];
    return Object.keys(responseData);
  }, [responseData]);

  // State for selected batch
  const [selectedBatch, setSelectedBatch] = useState<string>("");

  // Set default batch when data loads
  React.useEffect(() => {
    if (batchNames.length > 0 && !selectedBatch) {
      setSelectedBatch(batchNames[0]);
    }
  }, [batchNames, selectedBatch]);

  // Get the data for the selected batch (Target Assignment)
  const data = useMemo(() => {
    if (!responseData || !selectedBatch) return undefined;
    return responseData[selectedBatch];
  }, [responseData, selectedBatch]);

  // Get the cell data for the selected batch (Cell Assignment)
  const cellData = useMemo(() => {
    if (!responseCellData || !selectedBatch) return undefined;
    return responseCellData[selectedBatch];
  }, [responseCellData, selectedBatch]);

  // Get unique target site names from the selected batch data
  const targetSiteNames = useMemo(() => {
    if (!data?.TargetSiteAssignmentStats) return [];
    return data.TargetSiteAssignmentStats.map((stat) => stat.TargetSiteName);
  }, [data]);

  // State for selected target sites
  const [selectedTargetSiteAssignment, setSelectedTargetSiteAssignment] =
    useState<string>("");
  const [selectedTargetSiteCellAssignment, setSelectedTargetSiteCellAssignment] =
    useState<string>("");

  // Set default target site selection for Section 1 (Target Assignment)
  React.useEffect(() => {
    if (targetSiteNames.length > 0) {
      setSelectedTargetSiteAssignment(targetSiteNames[0]);
    }
  }, [targetSiteNames]);

  // ============ Target Site Assignment Data Processing ============

  // Process data for Rate Assigned chart (per well)
  const rateAssignedChartData = useMemo(() => {
    if (!data?.WellTargetAssignmentStats || !selectedTargetSiteAssignment) return [];

    return data.WellTargetAssignmentStats.map((well) => {
      const targetStats = well.TargetSiteAssignmentStats?.find(
        (stat) => stat.TargetSiteName === selectedTargetSiteAssignment
      );
      return {
        Well: well.WellLocation,
        RateAssigned: presentStat(targetStats?.RateAssigned),
      };
    });
  }, [data, selectedTargetSiteAssignment]);

  // Process data for Rate Mismatch chart (per well)
  const rateMismatchChartData = useMemo(() => {
    if (!data?.WellTargetAssignmentStats || !selectedTargetSiteAssignment) return [];

    return data.WellTargetAssignmentStats.map((well) => {
      const targetStats = well.TargetSiteAssignmentStats?.find(
        (stat) => stat.TargetSiteName === selectedTargetSiteAssignment
      );
      return {
        Well: well.WellLocation,
        RateMismatch: presentStat(targetStats?.RateMismatch),
      };
    });
  }, [data, selectedTargetSiteAssignment]);

  // Process unassigned sequences table data
  const unassignedSequencesData = useMemo(() => {
    if (!data?.TargetSiteAssignmentStats || !selectedTargetSiteAssignment) return [];

    const targetStats = data.TargetSiteAssignmentStats.find(
      (stat) => stat.TargetSiteName === selectedTargetSiteAssignment
    );

    if (!targetStats?.UnassignedSequences) return [];

    return targetStats.UnassignedSequences.map((seq, index) => ({
      Rank: index + 1,
      Sequence: seq.Sequence,
      OccurrenceRate: presentStat(seq.OccurrenceRate),
    })).slice(0, 20); // Show top 20
  }, [data, selectedTargetSiteAssignment]);

  // ============ Cell Assignment Stats Data Processing ============

  // Get target site names from cell data (might be different from target assignment)
  const cellTargetSiteNames = useMemo(() => {
    if (!cellData?.WellCellTargetAssignmentStats?.length) return [];
    
    // Find the first well that has TargetSiteCellAssignmentStats
    const wellWithData = cellData.WellCellTargetAssignmentStats.find(
      (well) => well.TargetSiteCellAssignmentStats && well.TargetSiteCellAssignmentStats.length > 0
    );
    
    if (!wellWithData?.TargetSiteCellAssignmentStats) return [];
    
    return wellWithData.TargetSiteCellAssignmentStats.map((stat) => stat.TargetSiteName);
  }, [cellData]);

  const selectedBatchControlSites =
    panelControls.status === "ready" && selectedBatch
      ? panelControls.byBatch.get(selectedBatch.trim())
      : undefined;

  const activeCellTargetSite = cellTargetSiteNames.includes(
    selectedTargetSiteCellAssignment
  )
    ? selectedTargetSiteCellAssignment
    : cellTargetSiteNames[0] ?? "";

  const showRateChart =
    panelControls.status === "unavailable" ||
    (panelControls.status === "ready" &&
      !!selectedBatch &&
      showsCellAssignmentRateChart(
        activeCellTargetSite,
        selectedBatchControlSites
      ));

  // Process data for stacked bar chart
  const cellAssignmentStackedData = useMemo(() => {
    if (!cellData?.WellCellTargetAssignmentStats || !activeCellTargetSite)
      return [];

    return cellData.WellCellTargetAssignmentStats.map((well) => {
      const targetStats = well.TargetSiteCellAssignmentStats?.find(
        (stat) => stat.TargetSiteName === activeCellTargetSite
      );

      return {
        Well: well.WellLocation,
        RateAssignedPureCells: presentStat(targetStats?.RateAssignedPureCells),
        RateAssignedMixedCells: presentStat(targetStats?.RateAssignedMixedCells),
        RateUnassignedMixedCells: presentStat(targetStats?.RateUnassignedMixedCells),
        RateUnassignedLowCountCells: presentStat(targetStats?.RateUnassignedLowCountCells),
      };
    });
  }, [cellData, activeCellTargetSite]);

  // Process data for cell stats table
  const cellStatsTableData = useMemo(() => {
    if (!cellData?.WellCellTargetAssignmentStats || !activeCellTargetSite)
      return [];

    return cellData.WellCellTargetAssignmentStats.map((well) => {
      const targetStats = well.TargetSiteCellAssignmentStats?.find(
        (stat) => stat.TargetSiteName === activeCellTargetSite
      );

      return {
        WellLocation: well.WellLocation,
        AssignedCountsPerMM2: presentStat(targetStats?.AssignedCountsPerMM2),
        MeanAssignedCountPerCell: presentStat(targetStats?.MeanAssignedCountPerCell),
        MedianAbundantTargetCount: presentStat(targetStats?.MedianAbundantTargetCount),
        MeanUniqueTargetsPerCell: presentStat(targetStats?.MeanUniqueTargetsPerCell),
        ExtraCellularRatio: presentStat(targetStats?.ExtraCellularRatio),
        RateTargetDropout: presentStat(targetStats?.RateTargetDropout),
      };
    });
  }, [cellData, activeCellTargetSite]);

  // ============ Loading and Error States ============

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading target assignment data...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">
        Error loading target assignment data: {String(error)}
      </Typography>
    );
  }

  if (!responseData || Object.keys(responseData).length === 0) {
    return <Typography>No target assignment data available for this run.</Typography>;
  }

  // ============ Render ============

  return (
    <Box
      sx={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
        pr: 2, // padding right for scrollbar
      }}
    >
      {/* ============ Batch Selector ============ */}
      {batchNames.length > 1 && (
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 4 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="batch-select-label">Batch</InputLabel>
            <Select
              labelId="batch-select-label"
              value={selectedBatch}
              label="Batch"
              onChange={(e) => setSelectedBatch(e.target.value)}
            >
              {batchNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* ============ Section 1: Target Site Assignment Stats ============ */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        Target Site Assignment Stats {selectedBatch && `- ${selectedBatch}`}
      </Typography>

      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="target-site-assignment-label">Target Site Name</InputLabel>
          <Select
            labelId="target-site-assignment-label"
            value={selectedTargetSiteAssignment}
            label="Target Site Name"
            onChange={(e) => setSelectedTargetSiteAssignment(e.target.value)}
          >
            {targetSiteNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Rate Assigned Chart */}
      <Chart 
        key={`rate-assigned-${selectedTargetSiteAssignment}`}
        dataSource={rateAssignedChartData} 
        style={{ marginTop: 10 }}
      >
        <Title
          horizontalAlignment="center"
          text={`Rate Assigned by Well - ${selectedTargetSiteAssignment}`}
        />
        <Size width="100%" />
        <CommonSeriesSettings argumentField="Well" type="bar" barPadding={0.1} />
        <Series valueField="RateAssigned" name="Rate Assigned" color="#4CAF50" />
        <ValueAxis title="Rate Assigned">
          <Grid visible={true} />
        </ValueAxis>
        <ArgumentAxis title="Well">
          <Label wordWrap="none" overlappingBehavior="stagger" />
        </ArgumentAxis>
        <Legend visible={false} />
        <Export enabled />
        <Tooltip enabled>
          <Format type="percent" precision={4} />
        </Tooltip>
      </Chart>

      {/* Rate Mismatch Chart */}
      <Chart 
        key={`rate-mismatch-${selectedTargetSiteAssignment}`}
        dataSource={rateMismatchChartData} 
        style={{ marginTop: 40 }}
      >
        <Title
          horizontalAlignment="center"
          text={`Rate Mismatch by Well - ${selectedTargetSiteAssignment}`}
        />
        <Size width="100%" />
        <CommonSeriesSettings argumentField="Well" type="bar" barPadding={0.1} />
        <Series valueField="RateMismatch" name="Rate Mismatch" color="#F44336" />
        <ValueAxis title="Rate Mismatch">
          <Grid visible={true} />
        </ValueAxis>
        <ArgumentAxis title="Well">
          <Label wordWrap="none" overlappingBehavior="stagger" />
        </ArgumentAxis>
        <Legend visible={false} />
        <Export enabled />
        <Tooltip enabled>
          <Format type="percent" precision={2} />
        </Tooltip>
      </Chart>

      {/* Unassigned Sequences Table */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Top Unassigned Sequences - {selectedTargetSiteAssignment}
        </Typography>
        <DataGrid
          dataSource={unassignedSequencesData}
          showBorders={true}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
        >
          <FilterRow visible={true} />
          <Paging defaultPageSize={10} />
          <GridExport enabled={true} />
          <Column dataField="Rank" caption="Rank" width={80} alignment="center" />
          <Column dataField="Sequence" caption="Unassigned Sequence" />
          <Column
            dataField="OccurrenceRate"
            caption="Rate"
            dataType="number"
            format={{ type: "percent", precision: 2 }}
            sortOrder="desc"
          />
        </DataGrid>
      </Box>

      {/* ============ Section 2: Cell Assignment Stats ============ */}
      {cellTargetSiteNames.length > 0 && (
        <>
          <Divider sx={{ my: 6 }} />

          <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
            Cell Assignment Stats (Well Cell Target Assignment)
          </Typography>

          {isLoadingCell && (
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography sx={{ ml: 1 }}>Loading cell assignment data...</Typography>
            </Box>
          )}

          {isErrorCell && (
            <Typography color="error" sx={{ mb: 2 }}>
              Error loading cell assignment data: {String(errorCell)}
            </Typography>
          )}

          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="target-site-cell-assignment-label">Target Site Name</InputLabel>
              <Select
                labelId="target-site-cell-assignment-label"
                value={activeCellTargetSite}
                label="Target Site Name"
                onChange={(e) => setSelectedTargetSiteCellAssignment(e.target.value)}
              >
                {cellTargetSiteNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {panelControls.status === "loading" && (
            <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
              <CircularProgress size={20} />
              <Typography sx={{ ml: 1 }}>
                Checking which target sites are controls...
              </Typography>
            </Box>
          )}

          {showRateChart && (
            <Chart dataSource={cellAssignmentStackedData} style={{ marginTop: 20 }}>
              <Title
                horizontalAlignment="center"
                text={`Cell Assignment Rates by Well - ${activeCellTargetSite}`}
              />
              <Size width="100%" />
              <CommonSeriesSettings argumentField="Well" type="stackedBar" barPadding={0.1} />
              <Series
                valueField="RateAssignedPureCells"
                name={RATE_FIELD_LABELS.RateAssignedPureCells}
                color={RATE_FIELD_COLORS.RateAssignedPureCells}
              />
              <Series
                valueField="RateAssignedMixedCells"
                name={RATE_FIELD_LABELS.RateAssignedMixedCells}
                color={RATE_FIELD_COLORS.RateAssignedMixedCells}
              />
              <Series
                valueField="RateUnassignedMixedCells"
                name={RATE_FIELD_LABELS.RateUnassignedMixedCells}
                color={RATE_FIELD_COLORS.RateUnassignedMixedCells}
              />
              <Series
                valueField="RateUnassignedLowCountCells"
                name={RATE_FIELD_LABELS.RateUnassignedLowCountCells}
                color={RATE_FIELD_COLORS.RateUnassignedLowCountCells}
              />
              <ValueAxis title="Rate">
                <Grid visible={true} />
              </ValueAxis>
              <ArgumentAxis title="Well">
                <Label wordWrap="none" overlappingBehavior="stagger" />
              </ArgumentAxis>
              <Legend
                verticalAlignment="bottom"
                horizontalAlignment="center"
                itemTextPosition="right"
              />
              <Export enabled />
              <Tooltip enabled>
                <Format type="percent" precision={2} />
              </Tooltip>
            </Chart>
          )}

      {/* Cell Stats Table */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Cell Assignment Statistics - {activeCellTargetSite}
        </Typography>
        <DataGrid
          dataSource={cellStatsTableData}
          showBorders={true}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
        >
          <FilterRow visible={true} />
          <Paging defaultPageSize={20} />
          <GridExport enabled={true} />
          <Column dataField="WellLocation" caption="Well" width={80} />
          <Column
            dataField="AssignedCountsPerMM2"
            caption="Assigned Counts/mm²"
            dataType="number"
            customizeText={(cell) =>
              formatMissingStat(cell.value, { precision: 2 })
            }
          />
          <Column
            dataField="MeanAssignedCountPerCell"
            caption="Mean Assigned Count/Cell"
            dataType="number"
            customizeText={(cell) =>
              formatMissingStat(cell.value, { precision: 2 })
            }
          />
          <Column
            dataField="MedianAbundantTargetCount"
            caption="Median Abundant Target Count"
            dataType="number"
            customizeText={(cell) =>
              formatMissingStat(cell.value, { precision: 2 })
            }
          />
          <Column
            dataField="MeanUniqueTargetsPerCell"
            caption="Mean Unique Targets/Cell"
            dataType="number"
            customizeText={(cell) =>
              formatMissingStat(cell.value, { precision: 2 })
            }
          />
          <Column
            dataField="ExtraCellularRatio"
            caption="Extra Cellular Ratio"
            dataType="number"
            customizeText={(cell) =>
              formatMissingStat(cell.value, { precision: 4 })
            }
          />
          <Column
            dataField="RateTargetDropout"
            caption="Target Dropout Rate"
            dataType="number"
            customizeText={(cell) =>
              formatMissingStat(cell.value, { precision: 2, style: "percent" })
            }
          />
        </DataGrid>
      </Box>
        </>
      )}
    </Box>
  );
};

export default CytoTargetAssignmentStats;
